import { useToast } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { useSimpleFormContext } from './SimpleForm';
import { SimplePopoverInput, SimplePopoverInputProps } from './SimplePopoverInput';

type Props<T = string[]> = Omit<SimplePopoverInputProps, 'displayValue' | 'name'> & {
  names?: string[];
  joiner?: string;
  suffix?: string;
  formatDisplayValue?: (values: T) => string;
  isHighlighted?: boolean;
  highlightColor?: string;
};

export const SimplePopoverUnifiedInput = ({
  label,
  children,
  names,
  joiner = '/',
  suffix = '',
  formatDisplayValue,
  ...rest
}: Props) => {
  const toast = useToast();
  const { validateField, onValid, submitOnBlur } = useSimpleFormContext();
  const { watch } = useFormContext();

  // Use flat map so we don't need to render multiple joiners later
  const values = names?.flatMap((name) => watch(name) || []) ?? [];

  // If there's no values, display "NA"
  const joinedValues = values.length ? `${values.join(joiner)}${suffix}` : 'NA';
  const displayValue = formatDisplayValue?.(values as any) ?? joinedValues;

  const onBlur = async () => {
    // Mirror SimpleLabeledPopoverInput: forms that apply values on submit
    // (submitOnBlur={false}) must not persist on popover close.
    if (!submitOnBlur) return;
    const fields: Record<string, any> = {};

    for (const name of names ?? []) {
      const field = await validateField(name);
      if (field) fields[name] = field[name];
    }

    const hasFields = Object.keys(fields).length > 0;
    if (!hasFields) return;

    try {
      await onValid(fields);
      toast({ title: 'Successfully updated', status: 'success' });
    } catch (error) {
      console.error(error);
      toast({
        title: "Oops, that didn't work",
        status: 'error',
        description: error?.message,
      });
    }
  };

  return (
    <SimplePopoverInput label={label} displayValue={displayValue} onPopoverBlur={onBlur} {...rest}>
      {children}
    </SimplePopoverInput>
  );
};
